import { describeFailure, type ApiFailure } from "@/services/api-client";

/**
 * Says where the figures on this page came from.
 *
 * In a credit product, sample data presented as though it were real is the
 * worst failure mode available — worse than an error, because nobody
 * investigates it. So a page that fell back says so, in the page, every time.
 *
 * Two forms, and a page uses both: a compact badge in the header that is always
 * present, and a full banner in the body that appears only when something went
 * wrong and the analyst needs the reason.
 *
 * Every fallback is now a warning. There used to be a quieter "not configured"
 * state for a build with no backend URL set; the backend serves this bundle, so
 * there is no such state any more — if a read fell back, something is wrong.
 */
export function DataSourceBadge({ fallback }: { fallback: ApiFailure | null }) {
  if (!fallback) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-positive">
        <span aria-hidden>●</span> Live from the backend
      </p>
    );
  }
  return (
    <p className="flex items-center gap-1.5 text-xs text-caution">
      <span aria-hidden>⚠</span> Sample data
    </p>
  );
}

export function DataSourceNotice({ fallback }: { fallback: ApiFailure | null }) {
  if (!fallback) return null;

  return (
    <div
      role="status"
      className="rounded-lg border border-caution/20 bg-caution-soft px-4 py-2.5 text-xs text-caution"
    >
      <span aria-hidden className="mr-1.5">
        ⚠
      </span>
      {describeFailure(fallback)}
    </div>
  );
}
